import React from 'react';

import { Box, Flex } from '@atlaskit/primitives/compiled';

import type { ContentMode } from '@confluence/content-types-utils/entry-points/getContentMode.ts';
import { isCanvasContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import {
	ContentTypeClassification,
	ContentTypeClassificationLoader,
} from '@confluence/data-classification/entry-points/ContentTypeClassification.tsx';
import { ClassificationEditModal } from '@confluence/data-classification/entry-points/ClassificationEditModal.ts';
import {
	useContentClassification,
	type ClassificationQueryContext,
} from '@confluence/data-classification/entry-points/queries.ts';
import { useGetClassificationTagUIVariant } from '@confluence/data-classification/entry-points/hooks.ts';
import { LoadableAfterPaint } from '@confluence/loadable';
import type { ShareContentType } from '@confluence/share/entry-points/ShareButton.tsx';
import type { FragmentRef } from '@confluence/apollo-relay-interop/entry-points/useFragmentizedQuery.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type { ContentTypesHeaderQueryNode } from '../ContentTypesHeader.tsx';
import { useContentBasicInfo } from '../useContentBasicInfo.ts';
import { useContentSpace } from '../useContentSpace.ts';

// duplicated from ContentHeader
const ContentClassificationTagLoader = LoadableAfterPaint({
	name: 'ContentClassificationTag',
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ContentClassificationTag" */ '@confluence/data-classification/entry-points/ContentClassificationTag.ts'
			)
		).ContentClassificationTag,
});

export type ContentTypesHeaderClassificationProps = {
	contentId: string;
	contentData?: ContentTypesHeaderQueryNode | null;
	contentBasicInfoRef?: FragmentRef;
	contentSpaceRef?: FragmentRef;
	contentStatusContext: ClassificationQueryContext;
	contentType: {
		type: ShareContentType;
		mode?: ContentMode;
	};
	readOnly: boolean;
	isSpaceOverviewPage: boolean;
	isObjectHeader: boolean;
};

export const ContentTypesHeaderClassification = ({
	contentId,
	contentData,
	contentBasicInfoRef,
	contentSpaceRef,
	contentStatusContext,
	contentType,
	readOnly,
	isSpaceOverviewPage,
	isObjectHeader,
}: ContentTypesHeaderClassificationProps) => {
	let spaceKey: string, contentBasicInfo;
	if (contentBasicInfoRef && contentSpaceRef) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		contentBasicInfo = useContentBasicInfo(contentBasicInfoRef);

		// eslint-disable-next-line react-hooks/rules-of-hooks
		spaceKey = useContentSpace(contentSpaceRef)?.key ?? '';
	} else {
		contentBasicInfo = contentData;
		spaceKey = contentData?.space?.key ?? '';
	}

	const contentClassificationMetadata = useContentClassification({
		contentId,
		spaceKeyOverride: spaceKey,
		contentStatusContext,
		shouldReadFromQueries: true,
	});

	const isClassificationEnabled = contentClassificationMetadata?.featureEnabled ?? false;

	const classificationTagUiVariant = useGetClassificationTagUIVariant({
		contentTitle: contentBasicInfo?.title ?? '',
		classificationName: contentClassificationMetadata.classification?.name,
	});

	if (
		(isCanvasContentType(contentType.type) ||
			contentType.type === 'database' ||
			contentType.type === 'embed' ||
			!isObjectHeader) && // from renderOriginalHeader in ContentTypesHeader
		isClassificationEnabled
	) {
		return (
			<Flex testId="content-types-header-classification">
				<ContentTypeClassification
					variant={classificationTagUiVariant}
					spaceKey={spaceKey}
					contentId={contentId}
					contentType={contentType.type}
					contentClassificationMetadata={contentClassificationMetadata}
					readOnly={readOnly}
				/>
				<ClassificationEditModal
					contentId={contentId}
					spaceKey={spaceKey}
					contentClassificationMetadata={contentClassificationMetadata}
				/>
			</Flex>
		);
	} else if (
		contentType.mode === 'live' ||
		contentType.mode === 'draft' ||
		contentType.mode === 'edit'
	) {
		return null;
	} else if (isSpaceOverviewPage) {
		// we want to render the classification tag in the header for space overview pages
		return (
			<Box testId="content-classification-tag">
				{expValEquals('dlp_auto_classifications_enabled', 'isEnabled', true) ? (
					<ContentTypeClassificationLoader
						variant={classificationTagUiVariant}
						spaceKey={spaceKey}
						contentId={contentId}
						contentType={contentType.type || null}
						contentClassificationMetadata={contentClassificationMetadata}
						readOnly={false}
					/>
				) : (
					<ContentClassificationTagLoader
						contentId={contentId}
						contentType={contentType.type || null}
						contentClassificationMetadata={contentClassificationMetadata}
						isEditMode={false}
					/>
				)}
			</Box>
		);
	}

	return null;
};
